import { Size } from "@brightagency/headstart-common";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBreakpoint } from "../../../controllers";
import { useListingCardCarouselSize } from "../../molecules/ListingCard/services";
import { Carousel, Props as CarouselProps } from "./main";

export const useCardCarousel = (
    contentCount: number,
    useItemSize: () => {
        itemWidth: number;
        itemGutter: number;
        totalItemWidth: number;
    } = useListingCardCarouselSize,
) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const { itemWidth, itemGutter, totalItemWidth } = useItemSize();
    const { scrollLeft, scrollRight, canScroll } = usePagedHorizontalScrolling({
        trackRef,
        pageWidth: totalItemWidth,
        contentCount,
    });

    const CarouselComponent = useCallback(
        (props: { children: ReactNode } & Partial<CarouselProps>) => {
            return (
                <Carousel
                    scrollingRef={trackRef}
                    itemWidth={itemWidth}
                    itemGutter={itemGutter}
                    {...props}
                >
                    {props.children}
                </Carousel>
            );
        },
        [itemGutter, itemWidth],
    );

    return {
        Carousel: CarouselComponent,
        scrollLeft,
        scrollRight,
        canScroll,
    };
};

export const usePagedHorizontalScrolling = ({
    trackRef,
    pageWidth,
    enableSnapping,
    desktopPagesAtOnce = 3,
    contentCount,
}: {
    trackRef: React.RefObject<HTMLDivElement | undefined>;
    pageWidth: number;
    enableSnapping?: boolean;
    desktopPagesAtOnce?: number;
    contentCount: number;
}) => {
    const isMedium = useBreakpoint(Size.medium);

    useEffect(() => {
        if (enableSnapping) {
            const onScrollEnd = debounce(() => {
                const SCROLL_TO_NEXT_THRESHOLD = 0.3;
                const track = trackRef.current;
                if (track) {
                    const { scrollLeft } = track;

                    if (track.scrollWidth - track.offsetWidth === track.scrollLeft) {
                        // If we're at the end of the track do nothing
                        return;
                    }

                    if (scrollLeft % pageWidth !== 0) {
                        const division = scrollLeft / pageWidth;
                        const leftover = division - Math.floor(division);
                        const numberOfUnitsScrolled =
                            Math.floor(division) + (leftover >= SCROLL_TO_NEXT_THRESHOLD ? 1 : 0);
                        const newScrollAmount = numberOfUnitsScrolled * pageWidth;
                        track.scrollTo({
                            left: newScrollAmount,
                            behavior: "smooth",
                        });
                    }
                }
            }, 250);

            const track = trackRef.current;
            if (track) {
                track.addEventListener("scroll", onScrollEnd);
                return () => {
                    track.removeEventListener("scroll", onScrollEnd);
                };
            }
        }
    }, [enableSnapping, pageWidth, trackRef]);

    const scrollRight = () => {
        if (trackRef.current) {
            trackRef.current.scrollBy({
                left: pageWidth * (isMedium ? desktopPagesAtOnce : 1),
                behavior: "smooth",
            });
        }
    };

    const scrollLeft = () => {
        if (trackRef.current) {
            trackRef.current.scrollBy({
                left: pageWidth * (isMedium ? desktopPagesAtOnce : 1) * -1,
                behavior: "smooth",
            });
        }
    };

    const resetScrollPosition = () => {
        if (trackRef.current) {
            trackRef.current.scrollTo({ left: 0 });
        }
    };

    const [canScroll, setCanScroll] = useState(true);
    const calculateCanScroll = useCallback(() => {
        if (trackRef.current) {
            setCanScroll(trackRef.current.scrollWidth > trackRef.current.clientWidth);
        }
    }, [trackRef]);
    useWindowResize(calculateCanScroll);
    useEffect(() => {
        calculateCanScroll();
    }, [contentCount, calculateCanScroll]);

    return { scrollRight, scrollLeft, resetScrollPosition, canScroll };
};

const useWindowResize = (handle: () => void) => {
    const throttledHandle = useMemo(() => throttle(handle, 250), [handle]);

    useEffect(() => {
        window.addEventListener("resize", throttledHandle);
        return () => {
            window.removeEventListener("resize", throttledHandle);
        };
    }, [throttledHandle]);
};
